import React from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryTopics } from '@/components/DiaryTopics';
import { LessonTypeChip } from '@/components/LessonTypeChip';
import { HomeTaskAttachments } from '@/components/HomeTaskModal/HomeTaskAttachments';
import { AuthorInfo } from '@/components/LessonDetailsModal/AuthorInfo';
import { DETAILS_CARD } from '@/components/LessonDetailsModal/classNames';
import type { Attachment } from '@/types/attachment';
import { AttachmentsDisplay } from '@/types/components';
import type { DiaryTopic } from '@/types/diary';
import type { EvaluationDetailLesson } from '@/types/overview';
import { sanitizeRichTextHtml } from '@/utils/html';

interface Props {
  lesson: EvaluationDetailLesson;
  diaryTopics?: DiaryTopic[];
  onPreview: (attachment: Attachment) => void;
}

export const EvaluationLessonCard: React.FC<Props> = ({
  lesson,
  diaryTopics,
  onPreview,
}) => {
  const { t } = useTranslation();

  const { type, lessonSubject, creation, lastModification } = lesson;
  const hasDiaryTopics = !!diaryTopics?.length;
  const isEvaluationSubjectShown = !diaryTopics;
  const attachments = diaryTopics
    ? diaryTopics.flatMap((topic) => topic.attachments)
    : lessonSubject.attachments;

  return (
    <div className={`flex flex-col gap-3 ${DETAILS_CARD}`}>
      <LessonTypeChip
        name={type.name}
        lessonTypeId={type.lessonTypeId}
        className="w-fit max-w-full"
      />

      {hasDiaryTopics && (
        <DiaryTopics
          isFullText
          topics={diaryTopics}
          attachmentsDisplay={AttachmentsDisplay.Hidden}
          className="text-body-14 text-foreground"
        />
      )}
      {!!diaryTopics && !hasDiaryTopics && (
        <p className="text-body-14 text-foreground">
          {t('diary.emptyDescription')}
        </p>
      )}

      {isEvaluationSubjectShown && !lessonSubject.text && (
        <p className="text-body-13 md:text-body-14 text-foreground">
          {t('diary.emptyDescription')}
        </p>
      )}
      {isEvaluationSubjectShown && !!lessonSubject.text && (
        <div
          className="rich-text text-body-13 md:text-body-14 text-foreground"
          dangerouslySetInnerHTML={{
            __html: sanitizeRichTextHtml(lessonSubject.text),
          }}
        />
      )}

      <HomeTaskAttachments
        isFullWidth
        attachments={attachments}
        onPreview={onPreview}
      />

      <AuthorInfo
        authorName={creation.authorName}
        timeCreated={creation.timeCreated}
        lastModification={lastModification}
      />
    </div>
  );
};
